import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './components/AuthProvider'
import { I18nProvider } from './i18n'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import Login from './pages/Login'
import Home from './pages/Home'
import Assets from './pages/Assets'
import AssetDetails from './pages/AssetDetails'
import Compliance from './pages/Compliance'
import CustomerTrust from './pages/CustomerTrust'
import GovernanceOverview from './pages/governance/GovernanceOverview'
import GovernancePolicies from './pages/governance/GovernancePolicies'
import GovernanceEvidence from './pages/governance/GovernanceEvidence'
import GovernanceAudits from './pages/governance/GovernanceAudits'
import GovernanceRisks from './pages/governance/GovernanceRisks'
import GovernanceTraining from './pages/governance/GovernanceTraining'
import GovernanceCalendar from './pages/governance/GovernanceCalendar'
import GovernanceNationalProfiles from './pages/governance/GovernanceNationalProfiles'
import GovernanceScoreAnalytics from './pages/governance/GovernanceScoreAnalytics'
import Suppliers from './pages/Suppliers'
import TPSM from './pages/TPSM'
import Reports from './pages/Reports'
import StarterGuide from './pages/StarterGuide'
import Integrations from './pages/Integrations'
import IntegrationsLayout, {
  IntegrationsCloudRoute,
  IntegrationsScannerRoute,
} from './pages/integrations/IntegrationsLayout'
import IntegrationsOverview from './pages/integrations/IntegrationsOverview'
import Sidebar from './components/Sidebar'
import { AppShellProvider } from './contexts/AppShellContext'
import { DefendSphereAiProvider, DefendSphereAiShell } from './components/defendsphere-ai'
import { MobileAppHeader } from './components/layout/MobileAppHeader'
import RestrictedScopeBanner from './components/governance/RestrictedScopeBanner'
import { NavDrawer } from './components/layout/NavDrawer'
import Help from './pages/Help'
import Settings from './pages/Settings'
import ComplianceSurvey from './pages/ComplianceSurvey'
import AdminPanel from './pages/AdminPanel'
import UserDashboard from './pages/UserDashboard'
import Notifications from './pages/Notifications'
import ScanRequests from './pages/ScanRequests'

import Organizations from './pages/Organizations'
import OrganizationSettingsWorkspace from './pages/OrganizationSettingsWorkspace'
import SupplierPortalView from './pages/tpsm/SupplierPortalView'
import AuditorPortal from './pages/AuditorPortal'
import ComplianceCenterLayout from './pages/compliance-center/ComplianceCenterLayout'
import ComplianceCenterOverview from './pages/compliance-center/ComplianceCenterOverview'
import ComplianceCenterOperationsWorkspace from './pages/compliance-center/ComplianceCenterOperationsWorkspace'
import ComplianceCenterAssessmentsWorkspace from './pages/compliance-center/ComplianceCenterAssessmentsWorkspace'
import ComplianceCenterEvidenceHub from './pages/compliance-center/ComplianceCenterEvidenceHub'
import ComplianceCenterRemediationWorkspace from './pages/compliance-center/ComplianceCenterRemediationWorkspace'
import ComplianceCenterRiskRegister from './pages/compliance-center/ComplianceCenterRiskRegister'
import ComplianceCenterAuditReadinessWorkspace from './pages/compliance-center/ComplianceCenterAuditReadinessWorkspace'
import ComplianceCenterCertificationWorkspace from './pages/compliance-center/ComplianceCenterCertificationWorkspace'
import ComplianceCenterCertificationWorkflowDetail from './pages/compliance-center/ComplianceCenterCertificationWorkflowDetail'
import ComplianceCenterAiAssistantWorkspace from './pages/compliance-center/ComplianceCenterAiAssistantWorkspace'
import ComplianceCenterAiGovernanceIntelligenceWorkspace from './pages/compliance-center/ComplianceCenterAiGovernanceIntelligenceWorkspace'
import GovernanceAiConversationWorkspace from './pages/compliance-center/GovernanceAiConversationWorkspace'
import ComplianceCenterExecutiveWorkspace from './pages/compliance-center/ComplianceCenterExecutiveWorkspace'
import ComplianceCenterTrustWorkspace from './pages/compliance-center/ComplianceCenterTrustWorkspace'
import ComplianceCenterGrcAdminWorkspace from './pages/compliance-center/ComplianceCenterGrcAdminWorkspace'
import ComplianceCenterAutomationWorkspace from './pages/compliance-center/ComplianceCenterAutomationWorkspace'
import ComplianceCenterControlLifecycleWorkspace from './pages/compliance-center/ComplianceCenterControlLifecycleWorkspace'
import ComplianceWorkflowPage from './pages/compliance-workflow/ComplianceWorkflowPage'
import ComplianceCenterCrossStandardReuseWorkspace from './pages/compliance-center/ComplianceCenterCrossStandardReuseWorkspace'
import ComplianceCenterOperationalAssuranceWorkspace from './pages/compliance-center/ComplianceCenterOperationalAssuranceWorkspace'
import ComplianceCenterOperationalEventsWorkspace from './pages/compliance-center/ComplianceCenterOperationalEventsWorkspace'
import ComplianceCenterExecutiveGovernanceWorkspace from './pages/compliance-center/ComplianceCenterExecutiveGovernanceWorkspace'
import { ComplianceWorkflowIndexProvider } from './contexts/ComplianceWorkflowIndexContext'
import { featureFlags } from './config/featureFlags'

function App() {
  const AppShell = () => (
    <div className="min-h-screen font-sans bg-background text-textMain">
      <AppShellProvider>
        <DefendSphereAiProvider>
        <MobileAppHeader />
        <Sidebar />
        <NavDrawer />
        <DefendSphereAiShell />
        <div className="main-content">
        <div className="px-4 pt-2 max-w-[1600px] mx-auto w-full">
          <RestrictedScopeBanner />
        </div>
        <ErrorBoundary>
          <ComplianceWorkflowIndexProvider>
          <Routes>
            <Route index element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/assets/:id" element={<AssetDetails />} />
            <Route path="/compliance" element={<Navigate to="/compliance-center/overview" replace />} />
            {featureFlags.COMPLIANCE_WORKFLOW_V1 ? (
              <Route path="/compliance-workflow" element={<ComplianceCenterLayout />}>
                <Route index element={<ComplianceWorkflowPage />} />
                <Route path=":standardCode" element={<ComplianceWorkflowPage />} />
              </Route>
            ) : (
              <Route path="/compliance-workflow/*" element={<Navigate to="/compliance-center/overview" replace />} />
            )}
            <Route path="/compliance/:standard/last-survey" element={<ComplianceSurvey />} />
            <Route path="/compliance-center" element={<ComplianceCenterLayout />}>
              <Route index element={<Navigate to="/compliance-center/overview" replace />} />
              <Route path="overview" element={<ComplianceCenterOverview />} />
              <Route path="assessments" element={<ComplianceCenterAssessmentsWorkspace />} />
              {featureFlags.CONTINUOUS_COMPLIANCE_OPERATIONS_V1 ? (
                <Route path="operations" element={<ComplianceCenterOperationsWorkspace />} />
              ) : null}
              <Route path="governance" element={<GovernanceOverview />} />
              <Route path="evidence" element={<ComplianceCenterEvidenceHub />} />
              {featureFlags.COMPLIANCE_REUSE_V1 ? (
                <Route path="cross-standard-reuse" element={<ComplianceCenterCrossStandardReuseWorkspace />} />
              ) : null}
              {featureFlags.CONTROL_ASSURANCE_V1 ? (
                <Route path="operational-assurance" element={<ComplianceCenterOperationalAssuranceWorkspace />} />
              ) : null}
              {featureFlags.COMPLIANCE_RUNTIME_V1 ? (
                <Route path="operational-events" element={<ComplianceCenterOperationalEventsWorkspace />} />
              ) : null}
              {featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
                <>
                  <Route path="executive-governance/:standardCode" element={<ComplianceCenterExecutiveGovernanceWorkspace />} />
                  <Route path="executive-governance" element={<ComplianceCenterExecutiveGovernanceWorkspace />} />
                </>
              ) : null}
              <Route path="policies" element={<GovernancePolicies />} />
              <Route path="remediation" element={<ComplianceCenterRemediationWorkspace />} />
              <Route path="risk-register" element={<ComplianceCenterRiskRegister />} />
              <Route path="audits" element={<GovernanceAudits />} />
              <Route path="audit-readiness" element={<ComplianceCenterAuditReadinessWorkspace />} />
              <Route path="certification" element={<ComplianceCenterCertificationWorkspace />} />
              <Route path="certification/:workflowId" element={<ComplianceCenterCertificationWorkflowDetail />} />
              <Route path="automation" element={<ComplianceCenterAutomationWorkspace />} />
              <Route path="control-lifecycle" element={<ComplianceCenterControlLifecycleWorkspace />} />
              <Route path="ai-assistant" element={<ComplianceCenterAiAssistantWorkspace />} />
              {/* AI-4: Phase 5A conversation always registered (auth/RBAC enforced by API). */}
              <Route path="ai-governance-conversation" element={<GovernanceAiConversationWorkspace />} />
              {featureFlags.AI_GOVERNANCE_INTELLIGENCE_SURFACE_V1 ? (
                <Route path="ai-governance-intelligence" element={<ComplianceCenterAiGovernanceIntelligenceWorkspace />} />
              ) : null}
              <Route path="executive" element={<ComplianceCenterExecutiveWorkspace />} />
              <Route path="trust-center" element={<ComplianceCenterTrustWorkspace />} />
              <Route path="grc-admin" element={<ComplianceCenterGrcAdminWorkspace />} />
            </Route>
            <Route path="/customer-trust" element={<CustomerTrust />} />
            <Route path="/governance" element={<GovernanceOverview />} />
            <Route path="/governance/overview" element={<GovernanceOverview />} />
            <Route path="/governance/policies" element={<GovernancePolicies />} />
            <Route path="/governance/evidence" element={<GovernanceEvidence />} />
            <Route path="/governance/audits" element={<GovernanceAudits />} />
            <Route path="/governance/risks" element={<GovernanceRisks />} />
            <Route path="/governance/training" element={<GovernanceTraining />} />
            <Route path="/governance/calendar" element={<GovernanceCalendar />} />
            <Route path="/governance/national-profiles" element={<GovernanceNationalProfiles />} />
            <Route path="/governance/score-analytics" element={<GovernanceScoreAnalytics />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/tpsm/*" element={<TPSM />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/starter-guide" element={<StarterGuide />} />
            <Route path="/help" element={<Help />} />
            <Route path="/integrations" element={<IntegrationsLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<IntegrationsOverview />} />
              <Route path="ai" element={<Integrations />} />
              <Route path="scanners" element={<IntegrationsScannerRoute />} />
              <Route path="scanners/:scanner" element={<IntegrationsScannerRoute />} />
              <Route path="google-cloud" element={<Navigate to="connection" replace />} />
              <Route path="google-cloud/:section" element={<IntegrationsCloudRoute />} />
            </Route>
            <Route path="/settings" element={<Settings />} />
            <Route path="/organization-settings" element={<OrganizationSettingsWorkspace />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/scan-requests" element={<ScanRequests />} />
            <Route path="/user-dashboard" element={<UserDashboard />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
          </ComplianceWorkflowIndexProvider>
        </ErrorBoundary>
      </div>
        </DefendSphereAiProvider>
      </AppShellProvider>
    </div>
  )
  return (
    <AuthProvider>
      <I18nProvider>
        <ErrorBoundary>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/supplier/submit/:token" element={<SupplierPortalView />} />
              <Route path="/auditor-portal" element={<AuditorPortal />} />
              <Route path="/*" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
            </Routes>
          </Router>
        </ErrorBoundary>
      </I18nProvider>
    </AuthProvider>
  )
}

export default App

